import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, ExternalLink, Search, Filter,
  TrendingUp, Target, Zap, Users, Building2, RefreshCw, BarChart2,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = process.env.REACT_APP_API_URL || "";

const AREA_COLOR = {
  brand:     { color: "#7C3AED", label: "Brand" },
  marketing: { color: "#00B4A0", label: "Marketing" },
  revops:    { color: "#F59E0B", label: "RevOps" },
};

const BAND_COLOR = {
  Strong: "#22C55E", Developing: "#00B4A0", Building: "#F59E0B", Critical: "#EF4444",
};

function ScorePill({ score, band }) {
  const color = BAND_COLOR[band] || "#6B7280";
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold"
          style={{ background: `${color}15`, color }}>
      {score}
      <span className="font-normal opacity-70">{band}</span>
    </span>
  );
}

function MiniBar({ score, color }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 rounded-full bg-gray-100">
        <div className="h-full rounded-full transition-all" style={{ width: `${score}%`, background: color }} />
      </div>
      <span className="text-xs text-gray-400 w-6 text-right">{score}</span>
    </div>
  );
}

function AssessmentCard({ item, onClick }) {
  const scores = item.scores || {};
  const meta   = item.meta || {};
  const completedAt = item.completed_at
    ? new Date(item.completed_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
    : "—";

  return (
    <button
      onClick={onClick}
      className="w-full text-left p-5 rounded-2xl border border-gray-100 bg-white hover:border-gray-200 hover:shadow-md transition-all group"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Building2 className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            <span className="text-sm font-semibold text-gray-900 truncate">{meta.company_name || "Unnamed company"}</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <span>{meta.company_type_label || meta.company_type}</span>
            <span>·</span>
            <Users className="w-3 h-3" />
            <span>{meta.role}</span>
            <span>·</span>
            <span>{completedAt}</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {scores.overall !== undefined && <ScorePill score={scores.overall} band={scores.overall_band} />}
          <ExternalLink className="w-3.5 h-3.5 text-gray-300 group-hover:text-[#7C3AED] transition-colors" />
        </div>
      </div>

      {scores.brand !== undefined && (
        <div className="space-y-1.5">
          <MiniBar score={scores.brand}     color={AREA_COLOR.brand.color} />
          <MiniBar score={scores.marketing} color={AREA_COLOR.marketing.color} />
          <MiniBar score={scores.revops}    color={AREA_COLOR.revops.color} />
          <div className="flex items-center gap-4 pt-1">
            {["brand", "marketing", "revops"].map(a => (
              <span key={a} className="flex items-center gap-1 text-[10px]" style={{ color: AREA_COLOR[a].color }}>
                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: AREA_COLOR[a].color }} />
                {AREA_COLOR[a].label}
              </span>
            ))}
          </div>
        </div>
      )}
    </button>
  );
}

function StatCard({ icon: Icon, color, label, value }) {
  return (
    <div className="p-4 rounded-xl border border-gray-100 bg-white shadow-sm">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${color}12` }}>
          <Icon className="w-3.5 h-3.5" style={{ color }} />
        </div>
        <span className="text-xs text-gray-500">{label}</span>
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    </div>
  );
}

export default function DashboardListPage() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState("");
  const [search, setSearch]     = useState("");
  const [filterType, setFilterType] = useState("all");

  const fetchAssessments = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/assessments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) { logout(); navigate("/login"); return; }
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to load assessments");
      setAssessments(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAssessments(); }, []); // eslint-disable-line

  const completed    = assessments.filter(a => a.status === "completed");
  const avgScore     = completed.length ? Math.round(completed.reduce((s, a) => s + (a.scores?.overall || 0), 0) / completed.length) : 0;
  const criticalCount = completed.filter(a => a.scores?.overall_band === "Critical").length;
  const companyTypes = [...new Set(assessments.map(a => a.meta?.company_type_label || a.meta?.company_type).filter(Boolean))];

  const filtered = assessments.filter(a => {
    const name = (a.meta?.company_name || "").toLowerCase();
    const type = a.meta?.company_type_label || a.meta?.company_type || "";
    return (!search || name.includes(search.toLowerCase())) && (filterType === "all" || type === filterType);
  });

  const handleLogout = () => { logout(); navigate("/"); };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      <nav className="fixed top-0 left-0 right-0 z-40 bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <img src="/ryvr-logo.png" alt="RYVR" className="h-7 w-auto" />
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400 hidden sm:block">{user?.name || user?.email}</span>
            <button onClick={fetchAssessments} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors" title="Refresh">
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
            <button onClick={handleLogout} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-700 transition-colors">
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-6 pt-24 pb-16">
        <div className="mb-8">
          <h1 className="text-2xl font-bold mb-1 text-gray-900">Assessment Dashboard</h1>
          <p className="text-sm text-gray-500">All client assessments — click any card to view the full analysis.</p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <StatCard icon={BarChart2}  color="#7C3AED" label="Total assessments" value={assessments.length} />
          <StatCard icon={Target}     color="#00B4A0" label="Completed"          value={completed.length} />
          <StatCard icon={TrendingUp} color="#F59E0B" label="Avg overall score"  value={completed.length ? avgScore : "—"} />
          <StatCard icon={Zap}        color="#EF4444" label="Critical gaps"       value={criticalCount} />
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by company name…"
              className="w-full h-9 pl-9 pr-3 rounded-lg bg-white border border-gray-200 text-sm text-gray-900 placeholder:text-gray-300 focus:outline-none focus:border-[#7C3AED] transition-colors"
            />
          </div>
          {companyTypes.length > 0 && (
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-300" />
              <select
                value={filterType}
                onChange={e => setFilterType(e.target.value)}
                className="h-9 pl-8 pr-8 rounded-lg bg-white border border-gray-200 text-sm text-gray-900 focus:outline-none focus:border-[#7C3AED] appearance-none cursor-pointer"
              >
                <option value="all">All company types</option>
                {companyTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400 text-sm">Loading assessments…</div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <p className="text-red-500 text-sm">{error}</p>
            <button onClick={fetchAssessments} className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm hover:bg-gray-200 transition-colors">Retry</button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-2 text-gray-400">
            <BarChart2 className="w-8 h-8 opacity-30" />
            <p className="text-sm">{assessments.length === 0 ? "No assessments yet." : "No results match your filters."}</p>
            {assessments.length === 0 && (
              <button onClick={() => navigate("/assess")} className="mt-2 px-4 py-2 rounded-lg bg-[#00B4A0] hover:bg-[#009383] text-white text-sm font-medium transition-colors">
                Run first assessment
              </button>
            )}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(item => (
              <AssessmentCard key={item._id || item.id} item={item} onClick={() => navigate(`/results/${item._id || item.id}`)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
